import { Box, IconButton, Stack, styled, Typography } from '@mui/material';
import { ConversationStatus } from '@provision/conversation-types';
import React, {
  memo,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useResizable } from 'react-resizable-layout';
import {
  BlockContainer,
  BlockContent,
  BlockHeader,
  BorderVerticalSeparator,
  CopyToClipboardButton,
  DynamicIcon,
  FormEditableText,
  Tooltip,
} from 'src/components';
import { GLOBAL_TABS_HEIGHT } from 'src/components/GlobalTabs';
import { CollapsingLayoutBlock } from 'src/components/Layout/collapsing-layout-block.view';
import { DrawerCloseControl } from 'src/components/Navbar/DesktopDrawer';
import {
  Conversation,
  useConversation,
  useGroupRules,
  useResolvedAddress,
  UserGroupRules,
} from 'src/graphql';
import { useConversationUpdate } from 'src/graphql/entities/conversation/use-conversation-update';
import { useMarkMessagesAsSeen } from 'src/graphql/entities/conversation-aggregations';
import { useUsersPresenceCached } from 'src/graphql/entities/user-presence/hooks/use-users-presence';
import { Chat, ConversationSidePanel } from 'src/pages/Conversations';
import { GridBreadcrumbItem } from 'src/pages/CustomViews/custom-views-selected-view/base-selected-view';
import useConversationViewsStore from 'src/store/conversation-views';
import { useScreenDimensions } from 'src/utils/system';
import { ConversationWindowHeader } from './SidePanel/ConversationWindow/conversation-window-header';

export const RIGHT_PANEL_MIN_WIDTH_PX = 250;
export const RIGHT_PANEL_WIDTH_PX = 370;
export const RIGHT_PANEL_MAX_WIDTH_PX = 550;

const GLASS_HEADER_BLUR = 'blur(14px) saturate(1.4)';

const ConversationGlassHeader = styled(BlockHeader, {
  name: 'ConversationGlassHeader',
})(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  zIndex: theme.zIndex.app.ServiceDeskConversationHeader,
  backgroundColor: 'transparent',
  backdropFilter: GLASS_HEADER_BLUR,
  WebkitBackdropFilter: GLASS_HEADER_BLUR,
}));

const NoticeMessageContainer = styled(Box, {
  name: 'NoticeMessageContainer',
})(({ theme }) => ({
  position: 'absolute',
  top: GLOBAL_TABS_HEIGHT,
  width: '100%',
  padding: theme.spacing(1),
  zIndex: '100',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: theme.palette.custom.OV.sys.primaryContainer,
}));

const NoticeMessageText = styled(Typography, {
  name: 'NoticeMessageText',
})(({ theme }) => ({
  color: theme.palette.custom.OV.sys.onPrimaryContainer,
}));

const ConversationContainer = styled(Stack, {
  name: 'ConversationContainer',
})(({ theme }) => ({
  position: 'relative',
  flexDirection: 'row',
  flex: 1,
  zIndex: theme.zIndex.app.ServiceDeskConversation,
  width: 0,
}));

const ConversationDetailsWrapper = styled(CollapsingLayoutBlock, {
  name: 'ConversationDetailsWrapper',
})(({ theme, open }) => ({
  position: 'static',
  [theme.breakpoints.down('sm')]: {
    position: 'absolute',
    top: 0,
    height: '100%',

    /*
        Avoid visual blending of conversation details block with the conversation
        view during opening/closing animation.
        `right` offset hides the border when panel is completely opened/closed.
      */
    right: -1,
    ...(!open ? {} : { right: 1 }),
  },
}));

interface ConversationProps {
  conversation: Conversation;
  onCloseConversation: () => void;
  onDeleteConversation?: () => void;
  noticeMessage?: string;
  onUpdate: (c: Conversation) => Promise<void>;
  isConversationExpanded?: boolean;
  isSplitView?: boolean;
  customViewId?: string | null;
  customViewName?: string | null;
}

const ConversationWindowComponent = ({
  isConversationExpanded,
  conversation,
  onCloseConversation,
  noticeMessage,
  onUpdate,
  customViewId,
  customViewName,
  isSplitView,
  onDeleteConversation,
}: ConversationProps): React.JSX.Element => {
  const { isMobile, isDesktop } = useScreenDimensions();
  const { conversationSidePanelStateOpen, setConversationSidePanelStateOpen } =
    useConversationViewsStore();

  useEffect(() => {
    if (isMobile) {
      setConversationSidePanelStateOpen(false);
    }
  }, [isMobile, setConversationSidePanelStateOpen]);

  const [isNoticeMessageClosed, setIsNoticeMessageClosed] = useState(false);
  const { updateConversation } = useConversationUpdate();
  const { resolvedAddress, loading: isResolvingAddress } = useResolvedAddress(
    conversation.address,
  );
  const resolvedConversation = useMemo(
    () =>
      resolvedAddress && conversation.address !== resolvedAddress
        ? { ...conversation, address: resolvedAddress }
        : conversation,
    [conversation, resolvedAddress],
  );

  const { submitConversationData, participants, loading } = useConversation({
    conversationId: resolvedConversation.conversationId,
    partnerId: resolvedConversation?.partnerId,
  });

  const { markAsSeenIfNeeded } = useMarkMessagesAsSeen(resolvedConversation.conversationId);

  useEffect(() => {
    void markAsSeenIfNeeded(resolvedConversation.conversationId);
  }, [resolvedConversation, markAsSeenIfNeeded]);

  const onSubmitEditedSubject = useCallback(
    async (data: { subject: string }) => {
      const { subject } = data;

      if (subject && resolvedConversation?.subject !== subject) {
        await updateConversation({ ...resolvedConversation, subject });
      }
    },
    [updateConversation, resolvedConversation],
  );

  const form = useForm({
    values: { subject: resolvedConversation?.subject },
  });

  useEffect(() => {
    form.reset({ subject: resolvedConversation?.subject });
    setIsNoticeMessageClosed(false);
  }, [resolvedConversation?.subject, form]);

  const conversationViewRef = useRef<HTMLDivElement | null>(null);

  const {
    position: sidePanelWidth,
    separatorProps,
    isDragging,
  } = useResizable({
    axis: 'x',
    initial: RIGHT_PANEL_WIDTH_PX,
    min: RIGHT_PANEL_MIN_WIDTH_PX,
    max: RIGHT_PANEL_MAX_WIDTH_PX,
    containerRef: conversationViewRef as RefObject<HTMLElement>,
    reverse: true,
    disabled: !conversationSidePanelStateOpen || isMobile,
  });

  const canResizeDetailsView = conversationSidePanelStateOpen && isDesktop;

  const { groupRules } = useGroupRules();

  const userIds = participants
    .filter((i) => i.participantEntity === 'platform_user' && i.participantIdentity)
    .map((i) => i.participantIdentity);

  const { userPresenceMap } = useUsersPresenceCached(userIds);

  const shouldDisableSubjectChange =
    isResolvingAddress ||
    (resolvedConversation.conversationStatusId as ConversationStatus) ===
      ConversationStatus.Closed ||
    groupRules[UserGroupRules.AccessToUpdateConversation] === false ||
    resolvedConversation.isDeleted;

  const closeSidePanel = useCallback(
    () => setConversationSidePanelStateOpen(false),
    [setConversationSidePanelStateOpen],
  );

  const isResizableSplitMode = isConversationExpanded && canResizeDetailsView;

  return (
    <ConversationContainer
      ref={conversationViewRef}
      sx={{
        userSelect: isDragging ? 'none' : 'auto',
      }}
    >
      <BlockContainer
        leftRoundedOnly={isResizableSplitMode}
        rightRoundedOnly={isSplitView}
        sx={{ minWidth: '20rem', position: 'relative' }}
      >
        <ConversationGlassHeader>
          <DrawerCloseControl />
          <Tooltip title="Close Conversation">
            <DynamicIcon
              name="right_panel_close"
              sx={{
                cursor: 'pointer',
                display: { sm: 'none' },
              }}
              onClick={onCloseConversation}
            />
          </Tooltip>
          {!isMobile && isConversationExpanded ? (
            <GridBreadcrumbItem
              handleClick={onCloseConversation}
              text={customViewName || 'All'}
            />
          ) : null}
          <CopyToClipboardButton
            content={resolvedConversation.friendlyId}
            successMessage={`Copied "${resolvedConversation.friendlyId}" to clipboard`}
          >
            {resolvedConversation.friendlyId}
          </CopyToClipboardButton>
          <Box flex="1" sx={{ overflow: 'hidden' }}>
            <FormProvider {...form}>
              <FormEditableText
                type="editable-text"
                defaultValue={resolvedConversation.subject}
                disabled={shouldDisableSubjectChange}
                name="subject"
                onBlur={form.handleSubmit(onSubmitEditedSubject)}
                textFieldProps={{
                  inputProps: {
                    maxLength: 200,
                  },
                }}
              />
            </FormProvider>
          </Box>
          <ConversationWindowHeader
            isConversationExpanded={isConversationExpanded}
            customViewId={customViewId}
            conversation={resolvedConversation}
            isConversationDetailsOpen={conversationSidePanelStateOpen}
            onConversationDetailsToggle={setConversationSidePanelStateOpen}
            handleAfterDelete={onDeleteConversation || onCloseConversation}
          />
        </ConversationGlassHeader>
        <BlockContent
          sx={{
            p: 0,
            justifyContent: 'end',
            position: 'static',
          }}
        >
          <Box
            display="flex"
            flex="1"
            flexDirection="row"
            justifyContent="space-between"
            overflow="auto"
          >
            <Box
              sx={{
                position: 'relative',
                width:
                  !isMobile && conversationSidePanelStateOpen && !isConversationExpanded
                    ? `calc(100% - ${Math.max(sidePanelWidth, 0)}px)`
                    : '100%',
                height: '100%',
              }}
            >
              {!loading && !!noticeMessage && !isNoticeMessageClosed && (
                <NoticeMessageContainer>
                  <NoticeMessageText variant="body2">{noticeMessage}</NoticeMessageText>
                  <IconButton onClick={() => setIsNoticeMessageClosed(true)}>
                    <DynamicIcon name="close" />
                  </IconButton>
                </NoticeMessageContainer>
              )}
              <Chat
                key={resolvedConversation.conversationId}
                conversation={resolvedConversation}
                participants={participants}
                userPresenceMap={userPresenceMap}
                onSubmitConversationData={submitConversationData}
              />
            </Box>

            {canResizeDetailsView && !isResizableSplitMode ? (
              <BorderVerticalSeparator {...separatorProps} />
            ) : null}

            {!isConversationExpanded ? (
              <ConversationDetailsWrapper
                open={conversationSidePanelStateOpen}
                sx={{
                  width: isMobile ? '100%' : sidePanelWidth,
                  ...(isDragging && { transition: 'none' }),
                  zIndex: isMobile ? 3 : undefined,
                  /*
                    In side-peek the details panel shares the container with the
                    floating header, so keep it below the header row — only the
                    thread is meant to pass behind the glass.
                  */
                  marginTop: GLOBAL_TABS_HEIGHT,
                  height: { xs: `calc(100% - ${GLOBAL_TABS_HEIGHT})`, sm: 'auto' },
                }}
              >
                <ConversationSidePanel
                  isConversationExpanded={isConversationExpanded}
                  onUpdate={onUpdate}
                  onClose={closeSidePanel}
                  conversation={resolvedConversation}
                />
              </ConversationDetailsWrapper>
            ) : null}
          </Box>
        </BlockContent>
      </BlockContainer>

      {isResizableSplitMode ? <BorderVerticalSeparator {...separatorProps} /> : null}

      {isConversationExpanded ? (
        <ConversationDetailsWrapper
          open={conversationSidePanelStateOpen}
          sx={{
            width: isMobile ? '100%' : sidePanelWidth,
            ...(isDragging && { transition: 'none' }),
            zIndex: 3,
            maxHeight: 'calc(100vh - 15px)',
          }}
        >
          <ConversationSidePanel
            isConversationExpanded={isConversationExpanded}
            onUpdate={onUpdate}
            onClose={closeSidePanel}
            conversation={resolvedConversation}
          />
        </ConversationDetailsWrapper>
      ) : null}
    </ConversationContainer>
  );
};

export const ConversationWindow = memo(ConversationWindowComponent);
